import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, FileArchive, Ban, Lock } from "lucide-react";

const AMOUNT_COLS = [
  ["payroll_year", "Year"], ["payroll_month", "Month"], ["basic_salary", "Basic"], ["gross_pay", "Gross"],
  ["employee_epf", "EPF EE"], ["employer_epf", "EPF ER"], ["employee_socso", "SOCSO EE"], ["pcb", "PCB"],
  ["net_pay", "Net"],
];

export default function PayrollArchiveTab() {
  const { can } = useAuth();
  const canImport = can("payroll_history:import");
  const canAmounts = can("payroll_history:view") && can("hr:view_sensitive");
  const [sub, setSub] = useState("archive");
  const [file, setFile] = useState(null);
  const [fields, setFields] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [batchName, setBatchName] = useState("");
  const [archive, setArchive] = useState([]);
  const [batches, setBatches] = useState([]);
  const [sources, setSources] = useState([]);
  const [srcForm, setSrcForm] = useState({ year: new Date().getFullYear(), month: "", document_type: "payroll_register", file_name: "", notes: "" });

  const loadArchive = () => api.get("/hr/payroll-archive").then(r => setArchive(r.data)).catch(() => {});
  const loadBatches = () => api.get("/hr/payroll-archive/batches").then(r => setBatches(r.data)).catch(() => {});
  const loadSources = () => api.get("/hr/payroll-archive/source-files").then(r => setSources(r.data)).catch(() => {});
  useEffect(() => {
    loadArchive(); loadBatches(); loadSources();
    if (canImport) api.get("/hr/payroll-archive/fields").then(r => { setFields(r.data.fields); setDocTypes(r.data.document_types); }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const doPreview = async () => {
    if (!file) return toast.error("Choose a file");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/hr/payroll-archive/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setHeaders(data.headers);
      toast.success(`Parsed ${data.summary.records} rows`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doImport = async () => {
    if (!batchName.trim()) return toast.error("Batch name required");
    try {
      const { data } = await api.post("/hr/payroll-archive/import", { batch_name: batchName, source_file: file?.name || "", rows: preview.rows });
      toast.success(`Archived ${data.inserted} records (${data.skipped} skipped)`);
      setPreview(null); setFile(null); setBatchName(""); loadArchive(); loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const voidRec = async (id) => {
    const reason = window.prompt("Void reason (audited):");
    if (!reason) return;
    try { await api.post(`/hr/payroll-archive/${id}/void`, { reason }); toast.success("Voided"); loadArchive(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addSource = async () => {
    try { await api.post("/hr/payroll-archive/source-files", { ...srcForm, year: Number(srcForm.year), month: srcForm.month ? Number(srcForm.month) : null });
      toast.success("Source file registered"); setSrcForm({ ...srcForm, file_name: "", notes: "" }); loadSources(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const SubTab = ({ id, label }) => (
    <button data-testid={`payroll-subtab-${id}`} onClick={() => setSub(id)}
      className={`px-3 py-1.5 text-sm rounded-md ${sub === id ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{label}</button>
  );

  return (
    <div data-testid="hr-payroll-tab">
      <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2.5 mb-3">
        <Lock className="w-3.5 h-3.5 inline mr-1" /> Historical Payroll is an <b>AutoCount archive</b> — READ-ONLY. It is never recalculated by the ERP payroll engine and original values are preserved. Corrections use void + re-import.
      </div>
      <div className="flex gap-2 mb-4">
        <SubTab id="archive" label="Archive" />
        <SubTab id="batches" label="Import Batches" />
        {canImport && <SubTab id="import" label="Import" />}
        <SubTab id="sources" label="Source Files" />
      </div>

      {sub === "archive" && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="excel-table w-full" data-testid="archive-table">
            <thead><tr><th>FAS ID</th><th>AutoCount</th><th>Year</th><th>Period</th><th>Basic</th><th>Gross</th><th>Net</th><th>Source</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {archive.map(r => (
                <tr key={r.id} data-testid={`archive-row-${r.id}`} className={r.status === "voided" ? "opacity-50 line-through" : ""}>
                  <td className="font-mono">{r.employee_id}</td>
                  <td className="font-mono text-xs">{r.external_autocount_employee_code}</td>
                  <td>{r.payroll_year}</td><td>{r.payroll_month}</td>
                  <td>{canAmounts ? r.basic_salary ?? "—" : "•••"}</td>
                  <td>{canAmounts ? r.gross_pay ?? "—" : "•••"}</td>
                  <td>{canAmounts ? r.net_pay ?? "—" : "•••"}</td>
                  <td><Badge variant="outline" className="text-xs">AutoCount</Badge></td>
                  <td>{r.status}</td>
                  <td className="text-right">{canImport && r.status !== "voided" && <button onClick={() => voidRec(r.id)} data-testid={`archive-void-${r.id}`}><Ban className="w-4 h-4 text-rose-500" /></button>}</td>
                </tr>
              ))}
              {archive.length === 0 && <tr><td colSpan={10} className="text-center text-slate-400 py-6">No historical payroll imported yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {sub === "batches" && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="excel-table w-full" data-testid="batches-table">
            <thead><tr><th>Batch</th><th>Source File</th><th>Records</th><th>Skipped</th><th>Imported By</th><th>At</th></tr></thead>
            <tbody>
              {batches.map(b => (<tr key={b.id}><td>{b.name}</td><td className="text-xs">{b.source_file}</td><td>{b.records_count}</td><td>{b.skipped}</td><td className="text-xs">{b.imported_by}</td><td className="text-xs">{b.imported_at?.slice(0, 16)}</td></tr>))}
              {batches.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-6">No import batches yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {sub === "import" && canImport && (
        <div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 mb-3">
            <Label>AutoCount export file (.xlsx or .csv)</Label>
            <div className="flex gap-2 mt-1">
              <Input type="file" accept=".xlsx,.csv" data-testid="payroll-file-input" onChange={e => setFile(e.target.files[0])} />
              <Button onClick={doPreview} data-testid="payroll-preview-button" className="bg-sky-600 hover:bg-sky-700"><Upload className="w-4 h-4 mr-1.5" />Preview</Button>
            </div>
            {headers.length > 0 && (
              <div className="mt-3">
                <Label className="text-xs">Map columns (choose which uploaded column feeds each field)</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {(fields || []).map(f => (
                    <div key={f} className="flex items-center gap-1 text-xs">
                      <span className="w-28 text-slate-500">{f}</span>
                      <Select value={mapping[f] || "none"} onValueChange={v => setMapping(m => ({ ...m, [f]: v === "none" ? undefined : v }))}>
                        <SelectTrigger className="h-7" data-testid={`map-${f}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">—</SelectItem>{headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="mt-2" onClick={doPreview} data-testid="payroll-remap-button">Re-preview with mapping</Button>
              </div>
            )}
          </div>

          {preview && (
            <div>
              <div className="flex items-center gap-3 mb-2 text-sm" data-testid="payroll-preview-summary">
                <Badge variant="outline">Records: {preview.summary.records}</Badge>
                <Badge variant="outline" className="text-emerald-700 border-emerald-300">Matched: {preview.summary.matched}</Badge>
                <Badge variant="outline" className="text-amber-700 border-amber-300">Unmatched: {preview.summary.unmatched}</Badge>
                <Badge variant="outline" className="text-rose-700 border-rose-300">Duplicates: {preview.summary.duplicates}</Badge>
                <Badge variant="outline" className="text-rose-700 border-rose-300">Errors: {preview.summary.errors}</Badge>
                <span className="ml-auto flex items-center gap-2">
                  <Input placeholder="Batch name" value={batchName} onChange={e => setBatchName(e.target.value)} className="h-8 w-48" data-testid="payroll-batch-name" />
                  <Button size="sm" onClick={doImport} data-testid="payroll-confirm-import" className="bg-slate-900">Confirm Import</Button>
                </span>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto max-h-96">
                <table className="excel-table w-full" data-testid="payroll-preview-table">
                  <thead><tr><th>AutoCount</th><th>Match</th><th>Year</th><th>Period</th><th>Net</th><th>Flags</th></tr></thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className={r.duplicate || r.errors?.length ? "bg-rose-50" : ""}>
                        <td className="font-mono text-xs">{r.autocount_employee_code}</td>
                        <td>{r.matched_fas_id ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">{r.matched_fas_id}</Badge> : <Badge variant="outline" className="text-amber-700 border-amber-300">unmatched</Badge>}</td>
                        <td>{r.payroll_year}</td><td>{r.payroll_month}</td><td>{r.net_pay ?? "—"}</td>
                        <td className="text-xs text-rose-600">{[r.duplicate ? "duplicate" : "", ...(r.errors || [])].filter(Boolean).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {sub === "sources" && (
        <div>
          {canImport && (
            <div className="flex items-end gap-2 mb-3 rounded-lg border border-slate-200 bg-white p-3">
              <div><Label>Year</Label><Input type="number" className="h-9 w-24" value={srcForm.year} onChange={e => setSrcForm({ ...srcForm, year: e.target.value })} data-testid="source-year" /></div>
              <div><Label>Month</Label><Input type="number" className="h-9 w-20" value={srcForm.month} onChange={e => setSrcForm({ ...srcForm, month: e.target.value })} placeholder="—" /></div>
              <div className="flex-1"><Label>Document type</Label>
                <Select value={srcForm.document_type} onValueChange={v => setSrcForm({ ...srcForm, document_type: v })}>
                  <SelectTrigger className="h-9" data-testid="source-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{docTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex-1"><Label>File name / reference</Label><Input className="h-9" value={srcForm.file_name} onChange={e => setSrcForm({ ...srcForm, file_name: e.target.value })} data-testid="source-filename" /></div>
              <Button onClick={addSource} data-testid="source-add-button" className="bg-sky-600 hover:bg-sky-700 h-9">Register</Button>
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="excel-table w-full" data-testid="sources-table">
              <thead><tr><th>Year</th><th>Month</th><th>Type</th><th>File</th><th>By</th></tr></thead>
              <tbody>
                {sources.map(s => (<tr key={s.id}><td>{s.year}</td><td>{s.month || "—"}</td><td><Badge variant="outline">{s.document_type}</Badge></td><td className="text-xs flex items-center gap-1"><FileArchive className="w-3.5 h-3.5 text-slate-400" />{s.file_name}</td><td className="text-xs">{s.uploaded_by}</td></tr>))}
                {sources.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-6">No source files registered. Structure is ready for your AutoCount report exports.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
